import PropTypes from "prop-types"
import React, { useState, useEffect, useMemo } from "react"
import { DndContext, closestCenter } from "@dnd-kit/core"
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import SortableQuestionItem from "./SortableQuestionItem"
import { Form, Input, Select, Button, Switch, Flex, Row, Col, Card } from "antd"
import { CloseCircleOutlined, DeleteOutlined, PlusCircleFilled } from "@ant-design/icons"
import styles from "./index.module.css"
import { Text } from "ui/Typography"
import {
  saveQuestionnaireTemplateQuestions,
  updateQuestionnaireTemplateQuestions,
  getQuestionnaireTemplateExperienceList
} from "store/question"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import IconWithBg from "ui/IconWithBg"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import DeleteConfirmation from "../../modal/DeleteConfirmation"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import ExperienceSelector from "pagesV2/pro/Equipment/experience/ExperienceSelector"
import { questionType, questionTypes, defaultOptions } from "../../Constant"
const { TextArea } = Input

// Generate a simple unique ID for new questions
const generateId = () => Math.random().toString(36).substring(2, 11)

const defaultQuestion = () => ({
  id: `q_${generateId()}`,
  question: "",
  type: "Paragraph",
  required: true,
  answer: "",
  options: structuredClone(defaultOptions)
})

const mapApiQuestionToLocal = (q) => ({
  id: q.id,
  question: q.question_text || "",
  type: q.question_type ? q.question_type.charAt(0).toUpperCase() + q.question_type.slice(1) : "Paragraph",
  required: q.required ?? true,
  answer: "", // You can map answer if you have it in API
  options: (q.questions_options || []).map((opt, idx) => ({
    id: opt.id,
    label: `Option ${String.fromCodePoint(65 + idx)}`,
    value: opt.option_text || ""
  }))
})

const CustomQuestions = ({ experienceList, description, trips, title, templateId, templateQuestions = [] }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [questions, setQuestions] = useState([defaultQuestion()])
  const [deleteModalState, setDeleteModalState] = useState({
    visible: false,
    questionId: null,
    optionToRemove: null,
    type: "question"
  })
  const [form] = Form.useForm()
  const [selectedExperiences, setSelectedExperiences] = useState([])
  useEffect(() => {
    if (trips) {
      const tripsId = trips.map((trip) => String(trip.id))
      setSelectedExperiences(tripsId)
      form.setFieldsValue({ experiences: tripsId })
    }
  }, [trips, form])
  const { loading } = useSelector((state) => state.question)
  // const { items } = useSelector((state) => state.trips)
  const { data: items } = useSelector((state) => state.question.templateExperienceList)
  const navigate = useNavigate()
  useEffect(() => {
    dispatch(getQuestionnaireTemplateExperienceList())
  }, [dispatch])
  // Helper variables for delete modal title/content
  let questionLabel = ""
  if (typeof deleteModalState.questionIndex === "number") {
    questionLabel = `Question ${deleteModalState.questionIndex + 1}`
  }
  let optionContentLabel = ""
  if (typeof deleteModalState.optionToRemove?.optionIndex === "number" && typeof deleteModalState.questionIndex === "number") {
    optionContentLabel = `Option ${String.fromCodePoint(65 + deleteModalState.optionToRemove.optionIndex)} in Question ${
      deleteModalState.questionIndex + 1
    }`
  }

  // Drag and drop handlers
  const handleDragEnd = (event) => {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      setQuestions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }
  useEffect(() => {
    if (Array.isArray(templateQuestions) && templateQuestions.length > 0) {
      setQuestions(templateQuestions.map(mapApiQuestionToLocal))
    }
  }, [templateQuestions])

  const addQuestion = () => {
    setQuestions([...questions, defaultQuestion()])
  }

  const handleDeleteClick = (questionId, type = "question", optionIndex = null, questionIndex = null) => {
    setDeleteModalState({
      visible: true,
      questionId,
      optionToRemove: type === "option" ? { questionId, optionIndex } : null,
      type,
      questionIndex
    })
  }

  const handleDeleteConfirm = () => {
    const { questionId, optionToRemove, type } = deleteModalState

    if (type === "question" && questionId && questions.length > 1) {
      setQuestions(
        questions
          .map((q) => {
            if (q.id === questionId) {
              // If question has id, mark _destroy, else remove
              if (q.id) return { ...q, _destroy: true }
              return null
            }
            return q
          })
          .filter(Boolean)
      )
    } else if (type === "option" && optionToRemove) {
      removeOption(optionToRemove.questionId, optionToRemove.optionIndex)
    }

    setDeleteModalState({
      visible: false,
      questionId: null,
      optionToRemove: null,
      type: "question"
    })
  }

  const handleDeleteCancel = () => {
    setDeleteModalState({
      visible: false,
      questionId: null,
      optionToRemove: null,
      type: "question"
    })
  }

  const updateQuestion = (id, field, value) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === id) {
          // If changing question type to one that needs options, initialize options if empty
          if (field === "type" && ["Checkbox", "Radio", "Dropdown"].includes(value) && (!q.options || q.options.length === 0)) {
            return {
              ...q,
              [field]: value,
              options: [
                { label: "Option A", value: "" },
                { label: "Option B", value: "" }
              ]
            }
          }
          return { ...q, [field]: value }
        }
        return q
      })
    )
  }

  const updateOption = (questionId, filteredIndex, value) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          // Get the actual index in the options array, considering deleted options
          const activeOptions = q.options.filter((opt) => !opt._destroy)
          if (filteredIndex < 0 || filteredIndex >= activeOptions.length) return q

          const optionToUpdate = activeOptions[filteredIndex]
          const actualIndex = q.options.indexOf(optionToUpdate)

          if (actualIndex === -1) return q

          // Create a new options array with the updated value
          const newOptions = [...q.options]
          newOptions[actualIndex] = {
            ...newOptions[actualIndex],
            value
          }

          return {
            ...q,
            options: newOptions,
            _updated: Date.now() // Force re-render
          }
        }
        return q
      })
    )
  }

  const addOption = (questionId) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          // Only count non-deleted options for the next label
          const activeOptions = q.options.filter((opt) => !opt._destroy)
          const nextLabel = String.fromCodePoint(65 + activeOptions.length)

          return {
            ...q,
            options: [...q.options, { label: `Option ${nextLabel}`, value: "" }],
            // Add a timestamp to force re-render of the options
            _updated: Date.now()
          }
        }
        return q
      })
    )
  }

  const removeOption = (questionId, filteredIndex) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          // Create a new array with only non-deleted options to find the actual index
          const activeOptions = q.options.filter((opt) => !opt._destroy)

          // If the index is invalid, return the question as is
          if (filteredIndex < 0 || filteredIndex >= activeOptions.length) {
            return q
          }

          // Find the actual index in the original options array
          const optionToRemove = activeOptions[filteredIndex]
          const actualIndex = q.options.indexOf(optionToRemove)

          if (actualIndex === -1) return q // Not found, return as is

          // Create a new options array with the option marked for deletion or removed
          const newOptions = [...q.options]
          if (optionToRemove.id) {
            // For existing options, mark for deletion
            newOptions[actualIndex] = { ...optionToRemove, _destroy: true }
          } else {
            // For new options, remove completely
            newOptions.splice(actualIndex, 1)
          }

          // Return a new question object to ensure re-render
          return {
            ...q,
            options: newOptions,
            // Add a timestamp to force re-render of the input field
            _updated: Date.now()
          }
        }
        return q
      })
    )
  }
  const validateQuestions = () => {
    let isValid = true
    const requiredFields = []

    questions.forEach((q, index) => {
      if (q._destroy) return

      if (q.required && !q.question.trim()) {
        requiredFields.push(`Question ${index + 1}`)
        isValid = false
      }

      if (["Dropdown", "Checkbox", "Radio"].includes(q.type)) {
        q.options.forEach((opt, optIndex) => {
          if (!opt._destroy && !opt.value.trim()) {
            requiredFields.push(`Option ${String.fromCodePoint(65 + optIndex)} in Question ${index + 1}`)
            isValid = false
          }
        })
      }
    })

    if (!isValid) {
      toaster.error(`Please fill in all required fields: ${requiredFields.join(", ")}`)
    }

    return isValid
  }
  const handleSubmit = async (status, values) => {
    try {
      // Validate required fields before submission (only for non-deleted questions)
      if (!validateQuestions()) {
        return
      }

      const questions_attributes = questions
        .filter((q) => !(q._destroy && q.id && typeof q.id === "string" && q.id.startsWith("q_")))
        .map((q, index) => {
          // For deleted questions, only include id and _destroy flag
          if (q._destroy) {
            return {
              id: q.id,
              _destroy: true
            }
          }

          // For active questions, include all the question data
          let questionData = {
            question_text: q.question,
            question_type: q.type.toLowerCase(),
            required: q.required,
            // position: index + 1,
            questions_options_attributes:
              q.type === "Paragraph"
                ? []
                : q.options
                    .filter((opt) => !opt._destroy || opt.id) // Keep deleted options that have an ID (existing in DB)
                    .map((opt, optIndex) => ({
                      ...(opt.id && { id: opt.id }),
                      ...(opt._destroy && { _destroy: true }),
                      ...(!opt._destroy && {
                        option_text: opt.value
                        // position: optIndex + 1
                      })
                    }))
          }

          // Include the ID for existing questions (not for new ones with temporary 'q_' prefix)
          if ((q.id && typeof q.id === "number") || (q.id && typeof q.id === "string" && !q.id.startsWith("q_"))) {
            questionData.id = q.id
          }

          return questionData
        })

      const payload = {
        questionnaire_template: {
          name: values.name,
          description: values.description,
          trip_ids: selectedExperiences.map((id) => Number.parseInt(id, 10)),
          questions_attributes: questions_attributes
        }
      }

      let res
      if (templateId) {
        res = await dispatch(updateQuestionnaireTemplateQuestions(templateId, payload))
        if (res.message) {
          toaster.success(res.message)
          navigate(routes.proSettingsCustomTemplate())
        }
      } else {
        res = await dispatch(saveQuestionnaireTemplateQuestions(payload))
        if (res.message) {
          toaster.success(res.message)
          navigate(routes.proSettingsCustomTemplate())
        }
      }
    } catch (error) {
      console.error("Error saving questions:", error)
    }
  }
  const experiences = useMemo(() => {
    return items.map((item) => ({
      ...item,
      name: item.title,
      status: item.status
    }))
  }, [items])
  const experineceListingData = useMemo(() => {
    return experienceList?.map((item) => ({
      ...item,
      name: item.title,
      status: item.status
    }))
  }, [experienceList])
  return (
    <div className={styles.container}>
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          // This will be called when form is valid
          handleSubmit("published", values)
        }}
        initialValues={{
          name: title || "",
          description: description || ""
        }}
        className={styles.container}
      >
        {/* <TemplateForm /> */}
        <Card className="mb-3xl">
          <div className="mt-3">
            <LabeledField
              labelKey="settings.template_labels_name"
              name="name"
              rules={[{ required: true, message: t("settings.required_template_name") }]}
            >
              <Input placeholder={t("settings.templates_placeholders_name")} />
            </LabeledField>
          </div>
          <LabeledField
            labelKey="settings.description"
            name="description"
            rules={[{ required: true, message: t("settings.required_description") }]}
          >
            <TextArea rows={4} placeholder={t("settings.description_placeholder")} />
          </LabeledField>
          <LabeledField name="experiences" labelKey={t("settings.experience")} className="mb-0">
            <ExperienceSelector
              experiences={templateId ? experineceListingData : experiences}
              value={selectedExperiences}
              onChange={(vals) => {
                const strVals = vals.map(String)
                // Only update if the values have actually changed
                if (strVals.length !== selectedExperiences.length || strVals.some((v, i) => v !== selectedExperiences[i])) {
                  setSelectedExperiences(strVals)
                  form.setFieldsValue({ experiences: strVals })
                }
              }}
              isShowExperience={true}
              style={{ width: "100%" }}
            />
          </LabeledField>
        </Card>
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={questions.filter((q) => !q._destroy).map((q) => q.id)} strategy={verticalListSortingStrategy}>
            {questions
              .filter((q) => !q._destroy)
              .map((q, idx) => (
                <SortableQuestionItem key={q.id} id={q.id}>
                  <div className={styles.questionCard}>
                    <Flex align="flex-start" gap={24}>
                      <Row gutter={[20, 20]} style={{ width: "100%" }}>
                        <Col span={18}>
                          <Flex vertical gap={6}>
                            <Text fw="500">{`Question ${idx + 1}*`}</Text>
                            <Form.Item
                              name={`question_${q.id}`}
                              initialValue={q.question}
                              rules={[
                                {
                                  required: true,
                                  message: t("settings.please_enter_a_question")
                                }
                              ]}
                            >
                              <Input
                                placeholder="What is your billing address?"
                                value={q.question}
                                onChange={(e) => updateQuestion(q.id, "question", e.target.value)}
                              />
                            </Form.Item>
                          </Flex>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="Question type" className={styles.formItem}>
                            <Select
                              value={q.type}
                              style={{ width: "100%" }}
                              onChange={(val) => updateQuestion(q.id, "type", val)}
                              options={questionTypes}
                            />
                          </Form.Item>
                        </Col>
                        {/* Render answer/options based on type */}
                        {q.type === questionType.PARAGRAPH && (
                          <Col span={24}>
                            <Form.Item label="Answer" style={{ marginBottom: 0 }}>
                              <Input.TextArea
                                placeholder="Enter text here"
                                autoSize={{ minRows: 4 }}
                                value={q.answer}
                                disabled={true}
                                onChange={(e) => updateQuestion(q.id, "answer", e.target.value)}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        {(q.type === questionType.DROPDOWN || q.type === questionType.CHECKBOX || q.type === questionType.RADIO) && (
                          <Col span={12}>
                            {q.options
                              .filter((opt) => !opt._destroy)
                              .map((opt, optIdx) => (
                                <div key={opt.id || `${opt.label}-${optIdx}`} className={styles.optionItem}>
                                  <div className={styles.optionLabel}>{`Option ${String.fromCodePoint(65 + optIdx)}*`}</div>
                                  <div className={styles.optionInputContainer}>
                                    <Form.Item
                                      name={opt.id || `${q.id}-${opt.label}-${optIdx}`}
                                      initialValue={opt.value}
                                      rules={[
                                        {
                                          required: true,
                                          message: t("settings.please_enter_option_text")
                                        }
                                      ]}
                                      style={{ width: "100%" }}
                                    >
                                      <Input
                                        placeholder="Enter text here"
                                        value={opt.value}
                                        onChange={(e) => updateOption(q.id, optIdx, e.target.value)}
                                        // className={`${styles.optionInput} ${styles.optionInputField}`}
                                      />
                                    </Form.Item>
                                    {((templateQuestions?.length > 0 &&
                                      (q.type === questionType.CHECKBOX ||
                                        q.type === questionType.RADIO ||
                                        q.type === questionType.DROPDOWN) &&
                                      q.options.filter((opt) => !opt._destroy).length > 2) ||
                                      (templateQuestions?.length === 0 &&
                                        (q.type === questionType.CHECKBOX ||
                                          q.type === questionType.RADIO ||
                                          q.type === questionType.DROPDOWN) &&
                                        optIdx >= 2)) && (
                                      <Button
                                        type="text"
                                        icon={<CloseCircleOutlined className={styles.removeOptionButton} />}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleDeleteClick(q.id, "option", optIdx, idx)
                                        }}
                                        className={styles.removeOptionBtn}
                                      />
                                    )}
                                  </div>
                                </div>
                              ))}
                            <Button
                              type="link"
                              icon={<PlusCircleFilled />}
                              onClick={() => addOption(q.id)}
                              className={styles.addOptionButton}
                            >
                              {t("settings.add_option")}
                            </Button>
                          </Col>
                        )}
                        <Col span={24}>
                          <div className={styles.requiredToggle}>
                            <Flex gap={10} align="center" className={styles.deleteQuestion}>
                              <Text fw="500">{t("settings.delete")}</Text>
                              {/* Disable delete when there is only one active question */}
                              <IconWithBg
                                icon={<DeleteOutlined className={styles.deleteIcon} />}
                                onClick={() => handleDeleteClick(q.id, "question", null, idx)}
                                disabled={questions.filter((qq) => !qq._destroy).length === 1}
                              />
                            </Flex>
                            <span className={styles.requiredLabel}>{t("settings.required")}</span>
                            <Switch checked={q.required} onChange={(checked) => updateQuestion(q.id, "required", checked)} />
                          </div>
                        </Col>
                      </Row>
                    </Flex>
                  </div>
                </SortableQuestionItem>
              ))}
          </SortableContext>
        </DndContext>
        <div className={styles.footerActions}>
          <button type="button" className={styles.addQuestionContainer} onClick={addQuestion} aria-label={t("settings.add_new_question")}>
            <PlusCircleFilled className={styles.addQuestionIcon} />
            <Text className={styles.addQuestionText}>{t("settings.add_new_question")}</Text>
          </button>
          <div className={styles.buttonGroup}>
            <Button htmlType="submit" color="primary" disabled={loading} loading={loading} variant="solid">
              {t("settings.save")}
            </Button>
          </div>
        </div>
      </Form>
      <DeleteConfirmation
        visible={deleteModalState.visible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={deleteModalState.type === "question" ? `${t("settings.delete_question")}` : `${t("settings.delete_option")}`}
        content={
          deleteModalState.type === "question" ? (
            <>
              <div>
                {t("settings.delete_confirmation")}
                <strong>{questionLabel}?</strong>
              </div>
              <div>{t("settings.delete_confirmation_desc")}</div>
            </>
          ) : (
            <>
              <div>
                {t("settings.delete_confirmation_option")}
                <strong>{optionContentLabel}?</strong>
              </div>
              <div>{t("settings.delete_confirmation_desc")}</div>
            </>
          )
        }
        confirmText={t("settings.delete")}
        cancelText={t("global.cancel")}
      />
    </div>
  )
}

export default CustomQuestions

CustomQuestions.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,

  templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  templateQuestions: PropTypes.arrayOf(PropTypes.object),
  experienceList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.string
    })
  ),
  trips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired
    })
  ).isRequired
}
